import React, { useState, useMemo } from "react";
import {
  Settings,
  Database,
  Cloud,
  ShieldCheck,
  RefreshCcw,
  Cpu,
  LayoutGrid,
} from "lucide-react";
import ManageMasters from "./ManageMasters";
import CloudSync from "./CloudSync";

const PlaceholderComponent = ({ title }) => (
  <div className="flex flex-col items-center justify-center h-64 border-2 border-dashed border-slate-200 rounded-xl bg-white text-slate-400">
    <LayoutGrid size={48} className="mb-4 opacity-20" />
    <h3 className="text-lg font-medium">{title} Component</h3>
    <p className="text-sm">This utility is currently under development.</p>
  </div>
);

const ApplicationUtilities = () => {
  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "[]"),
    [],
  );
  const isAdmin = currentUser[0]?.accessgrp?.trim()?.toUpperCase() === "ADMIN";

  const allMenuItems = [
    {
      id: "Cloud Sync",
      icon: <Cloud size={18} />,
      component: <CloudSync />,
      adminOnly: true,
    },
    {
      id: "P3",
      icon: <Cpu size={18} />,
      component: <PlaceholderComponent title="P3" />,
      adminOnly: true,
    },
    {
      id: "Accounts Transaction Integrity",
      icon: <ShieldCheck size={18} />,
      component: <PlaceholderComponent title="Transaction Integrity" />,
      adminOnly: true,
    },
    {
      id: "System Settings",
      icon: <Settings size={18} />,
      component: <PlaceholderComponent title="System Settings" />,
      adminOnly: true,
    },
    {
      id: "Manage Masters",
      icon: <Database size={18} />,
      component: <ManageMasters />,
      adminOnly: false,
    },
    {
      id: "Cost Refresh Utilities",
      icon: <RefreshCcw size={18} />,
      component: <PlaceholderComponent title="Cost Refresh" />,
      adminOnly: true,
    },
  ];

  const menuItems = useMemo(
    () => allMenuItems.filter((item) => isAdmin || !item.adminOnly),
    [isAdmin],
  );

  const [activeTab, setActiveTab] = useState("Manage Masters");

  const effectiveTab = menuItems.find((item) => item.id === activeTab)
    ? activeTab
    : menuItems[0]?.id;

  const activeContent = menuItems.find(
    (item) => item.id === effectiveTab,
  )?.component;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header Section */}
      <div className="bg-white border-b border-slate-200 px-5 py-3">
        <h1 className="text-xl font-bold text-slate-800">
          Application Utilities
        </h1>
        <p className="text-sm text-slate-500">
          System tools and data management consoles
        </p>
      </div>

      <div className="flex flex-1 flex-col lg:flex-row">
        {/* Navigation Sidebar */}
        <nav className="w-full lg:w-72 bg-white border-r border-slate-200 p-4 space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                effectiveTab === item.id
                  ? "bg-blue-600 text-white shadow-md shadow-blue-200"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              <span
                className={
                  effectiveTab === item.id ? "text-white" : "text-slate-400"
                }
              >
                {item.icon}
              </span>
              <span className="truncate">{item.id}</span>
            </button>
          ))}
        </nav>

        {/* Dynamic Content Area */}
        <main className="flex-1 p-4 bg-[#F8FAFC]">
          <div className=" mx-auto">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-700">
                {effectiveTab}
              </h2>
            </div>

            {/* Render the specific component here */}
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
              {activeContent}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ApplicationUtilities;
